export const ACCESS_TOKEN_KEY = 'BLOX_USER_TOKEN';
export const ACCESS_EXPIRY_KEY = 'BLOX_USER_TOKEN_EXPIRY';
export const REFRESH_TOKEN_KEY = 'BLOX_REFRESH_TOKEN';
export const REFRESH_EXPIRY_KEY = 'BLOX_REFRESH_TOKEN_EXPIRY';

export interface SessionTokenPayload {
  token: string;
  expiry?: string;
  refresh_token?: string;
  refresh_token_expiry?: string;
}

/** Backend expiry strings are UTC `"YYYY-MM-DD HH:MM"` (no seconds). */
export function parseExpiryToEpoch(expiry: string): number {
  return new Date(expiry.replace(/-/g, '/')).getTime();
}

function readStoredEpoch(key: string): number | null {
  const raw = localStorage.getItem(key);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw);
    const value = typeof parsed === 'number' ? parsed : Number(parsed);
    return Number.isFinite(value) ? value : null;
  } catch {
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  }
}

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function getAccessExpiry(): number | null {
  return readStoredEpoch(ACCESS_EXPIRY_KEY);
}

export function getRefreshExpiry(): number | null {
  return readStoredEpoch(REFRESH_EXPIRY_KEY);
}

export function persistAccessPair(token: string, expiry?: string) {
  if (expiry) {
    localStorage.setItem(ACCESS_EXPIRY_KEY, JSON.stringify(parseExpiryToEpoch(expiry)));
  }
  localStorage.setItem(ACCESS_TOKEN_KEY, token);
}

export function persistRefreshPair(refreshToken: string, refreshExpiry?: string) {
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  if (refreshExpiry) {
    localStorage.setItem(REFRESH_EXPIRY_KEY, JSON.stringify(parseExpiryToEpoch(refreshExpiry)));
  }
}

export function persistSessionTokens(payload: SessionTokenPayload) {
  persistAccessPair(payload.token, payload.expiry);
  if (payload.refresh_token) {
    persistRefreshPair(payload.refresh_token, payload.refresh_token_expiry);
  }
}

export function clearSessionTokens() {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(ACCESS_EXPIRY_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(REFRESH_EXPIRY_KEY);
}

export function isAccessTokenValid(skewMs = 0): boolean {
  const token = getAccessToken();
  const expiry = getAccessExpiry();
  if (!token || expiry == null) {
    return false;
  }
  return Date.now() + skewMs < expiry;
}

export function isRefreshTokenValid(): boolean {
  const token = getRefreshToken();
  const expiry = getRefreshExpiry();
  if (!token || expiry == null) {
    return false;
  }
  return Date.now() < expiry;
}

/** Valid access, or expired access with a still-valid refresh token. */
export function isSessionAuthenticated(): boolean {
  return isAccessTokenValid() || isRefreshTokenValid();
}
